import type { BatMetadataConfigEntry } from './common/types';
import { coreFields } from './common/coreFields';
import { msg } from '@lingui/core/macro';
import { locationCountryField } from './common/locationFields';

export const workersSocialMetadata = {
  displayName: msg({
    message: 'Workforce social data',
    context: 'Activity data schema name for workforce social data',
  }),
  fields: {
    ...coreFields,
    age: {
      displayName: msg({
        message: 'Age group',
        context: 'The age of employee group.',
      }),
      externalDescription: msg({
        message: 'The age of employee group.',
        context: 'Age group',
      }),
    },
    average_number_training_hours: {
      displayName: msg({
        message: 'Average number of training hours',
        context:
          'Average number of training hours the workers received in the period.',
      }),
      externalDescription: msg({
        message:
          'Average number of training hours the workers received in the period.',
        context: 'Average number of training hours',
      }),
    },
    currency: {
      displayName: msg({
        message: 'Wage currency',
        context: 'The currency of the workers wage.',
      }),
      externalDescription: msg({
        message: "The currency of the workers' wage.",
        context: 'Wage currency',
      }),
    },
    currency_quantity: {
      displayName: msg({
        message: 'Average hourly wage',
        context: 'The average hourly wage of the workers.',
      }),
      externalDescription: msg({
        message: 'The average hourly wage of the workers.',
        context: 'Average hourly wage',
      }),
    },
    employee_category: {
      displayName: msg({
        message: 'Employee category',
        context: 'The category of employee.',
      }),
      externalDescription: msg({
        message:
          'The category of employee (e.g. middle management, individual contributor)',
        context: 'Employee category',
      }),
    },
    employee_group_type: {
      displayName: msg({
        message: 'Employee group type',
        context: 'The type of employment for the group',
      }),
      externalDescription: msg({
        message:
          'The type of employment for the group (e.g. permanent, temporary)',
        context: 'Employee group type',
      }),
    },
    employment_type: {
      displayName: msg({
        message: 'Employment type',
        context: 'The type of employment for the group',
      }),
      externalDescription: msg({
        message:
          'The type of employment for the group (e.g. full-time, part-time, non-guaranteed hours)',
        context: 'Employment type',
      }),
    },
    gender: {
      displayName: msg({
        message: 'Gender',
        context: 'The gender of the worker',
      }),
      externalDescription: msg({
        message: 'The gender of the worker',
        context: 'Gender',
      }),
    },
    is_top_level_management: {
      displayName: msg({
        message: 'Is top-level management',
        context: 'If the employee is top-level management or not.',
      }),
      externalDescription: msg({
        message: 'If the employee is top-level management or not.',
        context: 'Is top-level management',
      }),
    },
    location_country: locationCountryField,
    non_employee_type: {
      displayName: msg({
        message: 'Non-employee type',
        context: 'The type of non-employee.',
      }),
      externalDescription: msg({
        message: 'The type of non-employee.',
        context: 'Non-employee type',
      }),
    },
    num_covered_by_health_and_safety_management_system: {
      displayName: msg({
        message: 'Workers covered by health and safety management system count',
        context:
          'Number of workers covered by the health and safety management systems',
      }),
      externalDescription: msg({
        message:
          'Number of workers covered by the health and safety management systems based on legal requirements and/or recognised standards or guidelines, as applicable to your company. NOTE: this is a snapshot of the workforce at the time of data upload, and can be thought of as a gauge, not a counter of activity.',
        context: 'Workers covered by health and safety management system count',
      }),
    },
    num_covered_by_workers_representation: {
      displayName: msg({
        message: 'Workers covered by workers representation count',
        context: 'Number of workers covered by workers representation.',
      }),
      externalDescription: msg({
        message: 'Number of workers covered by workers representation.',
        context: 'Workers covered by workers representation count',
      }),
    },
    num_covered_under_collective_bargaining: {
      displayName: msg({
        message: 'Workers covered under collective bargaining count',
        context:
          'Number of workers covered by collective bargaining agreements.',
      }),
      externalDescription: msg({
        message:
          'Number of workers covered by collective bargaining agreements.',
        context: 'Workers covered under collective bargaining count',
      }),
    },
    num_entitled_to_take_family_related_leave: {
      displayName: msg({
        message: 'Workers entitled to take family related leave count',
        context: 'Number of employees entitled to take family-related leave',
      }),
      externalDescription: msg({
        message:
          "Number of employees entitled to take family-related leave, which may include maternity leave, paternity leave, parental leave, carers' leave, etc, as applicable in your company or region of operation. NOTE: this is a snapshot of the workforce at the time of data upload, and can be thought of as a gauge, not a counter of activity.",
        context: 'Workers entitled to take family related leave count',
      }),
    },
    num_paid_below_adequate_wage: {
      displayName: msg({
        message: 'Number paid below adequate wage',
        context: 'Number of workers paid below the adequate wage benchmark.',
      }),
      externalDescription: msg({
        message: 'Number of workers paid below the adequate wage benchmark.',
        context: 'Number paid below adequate wage',
      }),
    },
    num_participated_in_reviews: {
      displayName: msg({
        message: 'Workers participated in performance reviews count',
        context:
          'Number of workers that participated in regular performance and career development reviews.',
      }),
      externalDescription: msg({
        message:
          'Number of workers that participated in regular performance and career development reviews. NOTE: this is a snapshot of the workforce at the time of data upload, and can be thought of as a gauge, not a counter of activity.',
        context: 'Workers participated in performance reviews count',
      }),
    },
    num_taken_family_related_leave: {
      displayName: msg({
        message: 'Workers who took family related leave quantity',
        context: 'Number of employees that took family-related leave',
      }),
      externalDescription: msg({
        message:
          "Number of employees that took family-related leave, which may include maternity leave, paternity leave, parental leave, carers' leave, etc, as applicable in your company or region of operation, during the period.",
        context: 'Workers who took family related leave quantity',
      }),
    },
    num_with_disabilities: {
      displayName: msg({
        message: 'Workers with disabilities count',
        context: 'The number of employees with disabilities.',
      }),
      externalDescription: msg({
        message:
          'The number of employees with disabilities. NOTE: this is a snapshot of the workforce at the time of data upload, and can be thought of as a gauge, not a counter of activity.',
        context: 'Workers with disabilities count',
      }),
    },
    num_workers: {
      displayName: msg({
        message: 'Workers count',
        context: 'The quantity of workers.',
      }),
      externalDescription: msg({
        message: 'The quantity of workers.',
        context: 'Workers count',
      }),
    },
    own_workforce_type: {
      displayName: msg({
        message: 'Own workforce type',
        context: 'Whether the group is made up of employees or non-employees',
      }),
      externalDescription: msg({
        message:
          'Whether the group is made up of employees or non-employees (i.e. contractors)',
        context: 'Own workforce type',
      }),
    },
    quantity_hours_worked: {
      displayName: msg({
        message: 'Quantity of hours worked',
        context:
          'The quantity of hours worked by the employees of the operation during the period.',
      }),
      externalDescription: msg({
        message:
          'The quantity of hours worked by the employees of the operation during the period.',
        context: 'Quantity of hours worked',
      }),
    },
    quantity_terminated_contract: {
      displayName: msg({
        message: 'Workers with terminated contract quantity',
        context:
          'The number of employees which terminated the contract during the period.',
      }),
      externalDescription: msg({
        message:
          'The number of employees which terminated the contract during the period.',
        context: 'Workers with terminated contract quantity',
      }),
    },
    region: {
      displayName: msg({
        message: 'Region',
        context: 'The region of the worker',
      }),
      externalDescription: msg({
        message: 'The region of the worker (e.g. EMEA)',
        context: 'Region',
      }),
    },
  },
} as const satisfies BatMetadataConfigEntry;
